import { DUMMY_ACCESS_RULES, mapEntityParams } from 'constants/entities';
import { ROOT_HIERARCHY_ID } from 'constants/hierarchy';
import { IMediaFile } from 'interfaces';
import { omit } from 'lodash';
import { Entities } from 'store/slices/mapV2/tabsReducer/layersReducer/mapEntitiesSlice/types';
import { EntityStorages, MapEntity, MapEntityTypes } from 'types';
import {
  AccessRules,
  Entity,
  EntityWithRelations,
  GeometryParameter,
  MediaParameter,
  NumberParameter,
  PredefinedTemplate,
  TextParameter,
} from 'types/entities';

import {
  createEmptyEntity,
  createEntityWithRelations,
  getDefaultMediaFile,
  getEntityParameterValue,
  getSelectOptionsFromEnumParam,
  replaceOrAppendArrayValue,
} from 'utils';

const getMapEntityType = (
  entity: EntityWithRelations,
  layerTemplateId: number,
  objectTemplateId: number
) => {
  switch (entity.entity.templateID) {
    case layerTemplateId:
      return MapEntityTypes.LAYER;
    case objectTemplateId:
    default:
      return MapEntityTypes.OBJECT;
  }
};

const getMapEntityPath = (entities: Entities, entityId: number): number[] => {
  const entity = entities[entityId];
  const parentId = entity?.parentIDs?.[0];

  if (!entity || !parentId) {
    return [ROOT_HIERARCHY_ID];
  }

  return [...getMapEntityPath(entities, parentId), parentId];
};

export const mapEntityTypeFilterFunc =
  (entityType: MapEntityTypes) => (entity: MapEntity) =>
    entity.info.type === entityType;

export const getSelectOptionsByMapEntityParam = (
  targetParam: mapEntityParams,
  mapObjectTemplate: PredefinedTemplate
) => {
  const param = mapObjectTemplate.parameters.find(
    (param) => param.title === targetParam
  );

  return param ? getSelectOptionsFromEnumParam(param) : [];
};

export const getMapObjectEntityValues = (
  parametersMap: Record<string, string>,
  mapObjectEntity: Entity
) => {
  const name = mapObjectEntity.title;

  const getParameterWithSetEntityAndMap = <T>(parameter: mapEntityParams) =>
    getEntityParameterValue<T>(mapObjectEntity, parametersMap, parameter);

  const type = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.TYPE
  );

  const status = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.STATUS
  );

  const geometry = getParameterWithSetEntityAndMap<GeometryParameter>(
    mapEntityParams.GEOMETRY
  );

  const date = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.DATE
  );

  const description = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.DESCRIPTION
  );

  const media =
    getParameterWithSetEntityAndMap<MediaParameter>(
      mapEntityParams.MEDIA
    )?.map?.((file: IMediaFile) => getDefaultMediaFile(file)) ?? null;

  const color = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.COLOR
  );

  const opacity = getParameterWithSetEntityAndMap<NumberParameter>(
    mapEntityParams.OPACITY
  );

  const source = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.SOURCE
  );

  const reliability = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.RELIABILITY
  );

  const relevance = getParameterWithSetEntityAndMap<TextParameter>(
    mapEntityParams.RELEVANCE
  );

  return {
    name,
    type,
    status,
    geometry,
    date,
    description,
    media,
    color,
    opacity,
    source,
    reliability,
    relevance,
  };
};

export const getMapEntityFullPath = (entities: Entities, entityId: number) => [
  ...getMapEntityPath(entities, entityId),
  entityId,
];

export const getMapEntityChildrenIds = (
  entities: Entities,
  entityId: number,
  filterFunc: (entity: MapEntity) => boolean = () => true
): number[] => {
  const entity: MapEntity | undefined = entities[entityId];

  if (!entity) {
    return [];
  }

  return entity.childIDs.reduce((acc, curr) => {
    const currEntity: MapEntity | undefined = entities[curr];

    if (!currEntity) {
      return acc;
    }

    const intermediateAcc = mapEntityTypeFilterFunc(MapEntityTypes.LAYER)(
      currEntity
    )
      ? [...acc, ...getMapEntityChildrenIds(entities, curr, filterFunc)]
      : acc;

    return intermediateAcc.concat(filterFunc(currEntity) ? [curr] : []);
  }, [] as number[]);
};

export const createBaseMapEntity = (
  entity: EntityWithRelations,
  info: MapEntity['info'],
  state: MapEntity['state']
): MapEntity => ({
  ...entity,
  info: info,
  state: state,
});

export const createMapEntity = (
  layerTemplateId: number,
  objectTemplateId: number,
  entity: EntityWithRelations,
  state: MapEntity['state']
) =>
  createBaseMapEntity(
    entity,
    { type: getMapEntityType(entity, layerTemplateId, objectTemplateId) },
    state
  );

export const createInitialMapEntity = (
  layerTemplateId: number,
  objectTemplateId: number,
  entity: Entity,
  accessRules: AccessRules,
  parentEntityID?: number
): MapEntity => {
  const entityWithRelations = createEntityWithRelations(
    entity,

    accessRules,
    parentEntityID
  );

  const initialState = {
    storage: EntityStorages.NONE,
    active: false,
  };

  return createMapEntity(
    layerTemplateId,
    objectTemplateId,
    entityWithRelations,
    initialState
  );
};

export const createMapDraftEntity = (
  layerTemplateId: number,
  objectTemplateId: number
): MapEntity => {
  const emptyEntity = createEmptyEntity(layerTemplateId);
  const mapEntity = createInitialMapEntity(
    layerTemplateId,
    objectTemplateId,
    emptyEntity,
    DUMMY_ACCESS_RULES
  );

  mapEntity.state.draft = true;

  return mapEntity;
};

export const insertMapEntity = (
  entities: Entities,
  entity: Entity
): Entities => ({
  ...entities,
  [String(entity.id)]: {
    ...entities[String(entity.id)],
    entity,
  },
});

export const insertMapEntities = (
  prevEntities: Entities,
  newEntities: Entities
): Entities => ({
  ...prevEntities,
  ...newEntities,
});

export const changeMapEntityParam = (
  mapEntity: MapEntity,
  paramIdMap: Record<string, string>,
  field: mapEntityParams,
  value: any
): MapEntity => ({
  ...mapEntity,
  entity: {
    ...mapEntity.entity,
    parameters: {
      ...mapEntity.entity.parameters,
      [paramIdMap[field]]: {
        value: value,
      },
    },
  },
});

export const updateMapEntitiesState = (
  entities: Entities,
  state: Partial<MapEntity['state']>,
  shouldUpdate: (entity: MapEntity) => boolean = () => true
): Entities =>
  Object.values(entities).reduce(
    (acc, curr) => ({
      ...acc,
      [curr.entity.id]: {
        ...curr,
        state: { ...curr.state, ...(shouldUpdate(curr) && state) },
      },
    }),
    {}
  );

export const updateNestedMapEntitiesState = (
  entities: Entities,
  id: string,
  state: Partial<MapEntity['state']>
): Entities => {
  const selectedEntity = entities[id];

  if (!selectedEntity) {
    return {};
  }

  const updatedState = {
    ...selectedEntity.state,
    ...state,
  };

  return {
    [selectedEntity.entity.id]: {
      ...selectedEntity,
      state: updatedState,
    },
    ...selectedEntity.childIDs.reduce(
      (acc, curr): Entities => ({
        ...acc,
        ...updateNestedMapEntitiesState(entities, String(curr), state),
      }),
      {}
    ),
  };
};

export const moveMapEntity = (
  entities: Entities,
  entityId: number,
  oldParentEntityId: number,
  newParentEntityId: number
) => {
  const entity = entities[entityId];
  const oldParentEntity = entities[oldParentEntityId];
  const newParentEntity = entities[newParentEntityId];

  return {
    ...entities,
    ...(oldParentEntity && {
      [oldParentEntityId]: {
        ...oldParentEntity,
        childIDs: oldParentEntity.childIDs.filter((id) => id !== entityId),
      },
    }),
    ...(entity && {
      [entityId]: {
        ...entity,
        parentIDs: replaceOrAppendArrayValue(
          entity.parentIDs,
          newParentEntityId,
          (id) => id === oldParentEntityId
        ),
      },
    }),
    ...(newParentEntity && {
      [newParentEntityId]: {
        ...newParentEntity,
        childIDs: [...newParentEntity.childIDs, entityId],
      },
    }),
  };
};

export const removeMapEntity = (entities: Entities, id: number) => {
  const entity: MapEntity | undefined = entities[id];
  const parentEntity: MapEntity | undefined = entities[entity?.parentIDs?.[0]];

  return omit(
    {
      ...entities,
      ...(parentEntity && {
        [parentEntity.entity.id]: {
          ...parentEntity,
          childIDs: parentEntity.childIDs.filter((ID) => ID !== id),
        },
      }),
    },
    [id, ...getMapEntityChildrenIds(entities, id)]
  ) as Entities;
};
